// Browser polyfills
import '@babel/polyfill';
import 'whatwg-fetch';

// Libs
import React from 'react';
import { connect } from 'react-redux';
import { getString, languageChangeEvent } from '@nackle/intl-tools';
import { Col, Row } from '@nackle/origami';

// Routing
import { Link, Route, routeHistory } from '@nackle/biw-web-router';
import { getMiddleware as getRouteMiddleware, routes } from './app-routing.jsx';
// Bridge
import { Bridge } from '@nackle/bridge';
// import appAuth from './app-auth.js';

import Header from './components/c-header/header.jsx';
import Branding from './components/c-branding/index.jsx';
import Fallback from './components/c-fallback/fallback.jsx';
import LanguagePicker from 'components/c-language-picker';
import ErrorDisplay from './components/c-error-display/index.jsx';
import ErrorBoundary from './components/c-error/error-boundary.jsx';
import LoadingOverlay from './components/c-loader/loading-overlay.jsx';

import { getCompany } from './app-dux/company-dux.js';
import { getSolutions } from './app-dux/choice-dux.js';
import { getPrimaryExperienceUrl } from './app-dux/interaction-dux';
import { getContent, getLocales, updateLocale } from './app-dux/app-dux.js';
import umbrella from '@nackle/umbrella';
import { getEnv, idTestAppUrl, idURL } from 'endpoints';
import { DEFAULT_LOCALE_CODE } from './constants.js';


import '@nackle/origami/dist/css/origami.css';

const DEFAULT_WINDOW_TITLE_KEY = 'kadhavu.pageTitle.default';
const DEFAULT_WINDOW_TITLE = 'Login'; // used IFF we failed to obtain the bundle

/*******************************************************************************
 *	1. App (root level component)
 */

const getLocaleCode = ( state, props ) => {
    const {
        company,
        updateLocale,
        userSelectedLocale
    } = props;

    let userSessionLocale = '';

    if ( sessionStorage && sessionStorage.getItem( 'userSessionLocale' ) !== null ) {
        userSessionLocale = sessionStorage.getItem( 'userSessionLocale' );

        if ( userSessionLocale !== userSelectedLocale ) {
            updateLocale( userSessionLocale );
        }
    }

    return (
        userSessionLocale ||                                            // first try to use locale from user's session
        userSelectedLocale ||                                           // fall back to user selected locale
        ( company ? company.defaultLocaleCode : null ) ||               // fall back to use the company default locale
        DEFAULT_LOCALE_CODE                                             // fall back to en-US
    );
};

class App extends React.Component {
    state = {
        contentFetched: false,
        contentFetchInitiated: false
    };

    componentDidMount() {
        try {
            this.props.getCompany();
            this.props.getLocales();
            this.props.getSolutions();
        } catch ( err ) {
            console.error( 'error when fetching default resource data:', err );
        }

        if ( window.location.pathname.indexOf( '/door/update-password' ) !== -1 ) {
            routeHistory.push( '/door/login' );
        }
        languageChangeEvent.on( ( bundle ) => {
            this.setState( {
                contentFetched: true,
                contentFetchLocaleCode: bundle.localeCode
            } );
        } );
    }

    static getDerivedStateFromProps( props, state ) {
        const localeCode = getLocaleCode( state, props );
        if ( ( state.contentFetched === false && state.contentFetchInitiated === false ) || localeCode !== state.contentFetchLocaleCode ) {
            props.getContent( localeCode );
            state.contentFetchInitiated = true;
            state.contentFetchLocaleCode = localeCode;
        }
        return state;
    }

    setWindowTitle = ( route ) => {
        const {
            solutionName
        } = this.props;

        const routeTitleKey = route && route.match ? route.match.title : DEFAULT_WINDOW_TITLE_KEY;
        let translatedTitle = getString( routeTitleKey );
        if ( translatedTitle.indexOf( 'Missing locale value' ) >= 0 ) {
            translatedTitle = DEFAULT_WINDOW_TITLE;
        }

        if ( String( solutionName ).trim().length > 0 ) {
            translatedTitle = `${ solutionName } | ${ translatedTitle }`;
        }

        document.title = translatedTitle;
    };

    handleOnSetLocation = async ( args ) => {
        const { getPrimaryExperienceUrl } = this.props;
        if ( args.type === 'sign_in' && args.location.indexOf( 'email-expired/' ) >= 0 ) {
            args.preventDefault();
            let primaryExperienceUrl = null;
            try {
                primaryExperienceUrl = await getPrimaryExperienceUrl();
            } catch ( err ) {
                console.error( 'error when fetching primary exp', err );
            }

            window.location = primaryExperienceUrl || '/error';
        }
    };

    getAuth = () => {
        const { idUrl } = this.props;
        let auth;
        // for local kadhavu end to end flow testing we need to set some url which point to local id-provider service
        if ( getEnv() === 'local' ) {
            const clientId = 'cpd-mlynar';
            const authAdditionalParams = {
                authority: idURL,
                clientId: clientId,
                redirectUri: `${ idTestAppUrl }/client/${ clientId }/oidc_signin`,
                postLogoutRedirectUri: `${ idTestAppUrl }/client/${ clientId }/oidc_signout`,
                silentRedirectUri: `${ idTestAppUrl }/client/${ clientId }/oidc_renew`,
                monitorSession: true,
                checkRecoveryMethods: false
            };
            auth = umbrella.getAuth( false, undefined, undefined, authAdditionalParams );
        } else {
            const authAdditionalParams = {
                authority: idUrl ? idUrl : idURL,
                checkRecoveryMethods: false // This will make sure not to check if user have recovery methods or not as the experiences will take care of it
            };
            auth = umbrella.getAuth( false, undefined, undefined, authAdditionalParams );
        }
        return auth;
    };

    render() {
        const { contentFetched, user } = this.state;
        const { company, loading, isUmbrella, idUrl } = this.props;
        const auth = this.getAuth();
        return (
            <React.Suspense fallback={ <Fallback /> }>
                <ErrorBoundary className="main-container">
                    <Route render={ ( route ) => {
                        const requiresAuth = route.match ? ( route.match.requireAuthentication || route.match.requiresAuth ) : false;
                        const isBackButtonVisible = route.match ? route.match.requireBackButton : false;
                        this.setWindowTitle( route );

                        return (
                            <>
                                <Bridge
                                    auth={ auth }
                                    requiresAuth={ requiresAuth }
                                    onSignIn={ ( user ) => this.setState( { user } ) }
                                    onSetLocation={ ( args ) => this.handleOnSetLocation( args ) }
                                >
                                    <Branding>
                                        <div className="main-container" role="main">
                                            {
                                                isBackButtonVisible &&
                                                <span tabIndex={ 0 } className="back-button" aria-label={ getString( 'common.backButton.ariaLabel' ) } > { /* eslint-disable-line */ }
                                                    <Link back={ true }>
                                                        <ArrowLeft />
                                                    </Link>
                                                </span>
                                            }
                                            { route.pathname !== '/error' && <LoadingOverlay loading={ loading } /> }
                                            {
                                                route.match ?
                                                    ( contentFetched || route.pathname === '/door/error' ) &&
                                                    <div className="form-container">
                                                        <Row align="middle">
                                                            <Col className="inner-form-container">
                                                                <div className="content-container">
                                                                    { /* Language Picker used across all pages to handle internationalization */ }
                                                                    <LanguagePicker />

                                                                    { /* Logo of the company */ }
                                                                    { route.pathname !== '/door/error' && <Header branding={ company.companyBranding } /> }

                                                                    { /* Error message across all routes will be shown here */ }
                                                                    <ErrorDisplay path={ route.pathname } />

                                                                    { /* Complete Form Component */ }
                                                                    {
                                                                        ( !requiresAuth
                                                                            ? React.cloneElement( route.component, { company, isUmbrella, idUrl } )
                                                                            : ( user
                                                                                ? React.cloneElement( route.component, { user, company, isUmbrella } )
                                                                                : null
                                                                            )
                                                                        )
                                                                    }
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                    : route.pathname && contentFetched && <ErrorBoundary error="path" />
                                            }
                                        </div>
                                    </Branding>
                                </Bridge>
                            </>
                        );
                    } } />
                </ErrorBoundary>
            </React.Suspense>
        );
    }
}

export const ArrowLeft = ( { originalSize, style, svgRef, className, ...props } ) => (
    <svg className={ className } width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.8287 5.00023V7.00023L4.00042 7.00023L7.24294 10.2428L5.82873 11.657L0.171875 6.00012L5.82873 0.343262L7.24294 1.75747L4.00019 5.00023L16.8287 5.00023Z" fill="#444A53" />
    </svg>
);

import './styles/style.less';
import AppStore from './app-store';

/*******************************************************************************
 *	2. Connect Routing
 */

App = connect(
    // mapStateToProps
    ( state ) => {
        return {
            loading: state.app.loading,
            company: state.company,
            solutionName: state.choice.solutionName,
            userSelectedLocale: state.app.userSelectedLocale
        };
    },
    // mapDispatchToProps
    {
        getCompany,
        getContent,
        getLocales,
        getSolutions,
        updateLocale,
        getPrimaryExperienceUrl
    }
)( App );

routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( getRouteMiddleware( AppStore() ) );

export default App;